import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SUBSCRIPTION_BOX_COUNTS = new Map([
  [54093911261556, 1],
  [54093911294324, 2],
  [54093911327092, 3],
]);
const BLACK_BOTTLE_VARIANT_ID = 55122513297780;
const WHITE_BOTTLE_VARIANT_ID = 55122513330548;
const CREATOR_GIFT_VARIANT_ID = 55173268308340;
const SUBSCRIPTION_GIFT_PROPERTY = '_2thrv_subscription_gift';
const CREATOR_GIFT_PROPERTY = '_2thrv_creator_gift';
const PROMO_GIFT_PROPERTY = '_2thrv_promo_gift';
const SUBSCRIPTION_GIFT_VARIANT_IDS = new Set([BLACK_BOTTLE_VARIANT_ID, WHITE_BOTTLE_VARIANT_ID]);
const FULL_BOX_VARIANT_IDS = new Set(SUBSCRIPTION_BOX_COUNTS.keys());
const CREATOR_REFERRAL_STORAGE_KEY = '2thrv_creator_referral';

let activeSync = null;
let syncQueued = false;
let queuedCartItems = null;
let creatorGiftValidationPending = false;

function isSubscriptionGiftLine(item) {
  return (
    SUBSCRIPTION_GIFT_VARIANT_IDS.has(Number(item.variant_id)) &&
    String(item.properties?.[SUBSCRIPTION_GIFT_PROPERTY]) === 'true'
  );
}

function isCreatorGiftLine(item) {
  return (
    Number(item.variant_id) === CREATOR_GIFT_VARIANT_ID &&
    String(item.properties?.[CREATOR_GIFT_PROPERTY]) === 'true'
  );
}

function isAuthorizedCreatorVariantLine(item) {
  if (Number(item.variant_id) !== CREATOR_GIFT_VARIANT_ID) return false;
  return isCreatorGiftLine(item) || String(item.properties?.[PROMO_GIFT_PROPERTY]) === 'true';
}

function cartLineIdentifier(item) {
  return item.key || item.id || item.variant_id;
}

function hasQualifyingSubscription(cart) {
  const subscriptionBoxes = cart.items.reduce((total, item) => {
    if (!item.selling_plan_allocation) return total;
    const boxesPerUnit = SUBSCRIPTION_BOX_COUNTS.get(Number(item.variant_id)) || 0;
    return total + boxesPerUnit * (Number(item.quantity) || 0);
  }, 0);
  return subscriptionBoxes >= 3;
}

function hasQualifyingCreatorPurchase(cart) {
  return cart.items.some((item) => !isCreatorGiftLine(item) && FULL_BOX_VARIANT_IDS.has(Number(item.variant_id)));
}

function creatorGiftIsFree(item) {
  const original = Number(item.original_line_price ?? item.original_price ?? 0);
  const final = Number(item.final_line_price ?? item.final_price ?? original);
  const allocated = Array.isArray(item.discount_allocations)
    ? item.discount_allocations.reduce((total, allocation) => total + Number(allocation.amount || 0), 0)
    : 0;
  return original > 0 && (final === 0 || allocated >= original);
}

function rememberReferralFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('ref') || params.has('affiliate') || params.has('creator')) {
    sessionStorage.setItem(CREATOR_REFERRAL_STORAGE_KEY, 'true');
  }

  const creatorCode = String(params.get('creator_code') || '').trim();
  if (!creatorCode || !/^[a-z0-9_-]{3,64}$/i.test(creatorCode)) return;

  sessionStorage.setItem(CREATOR_REFERRAL_STORAGE_KEY, 'true');
  params.delete('creator_code');
  const cleanQuery = params.toString();
  const cleanDestination = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '');
  window.location.replace(
    `/discount/${encodeURIComponent(creatorCode)}?redirect=${encodeURIComponent(cleanDestination)}`
  );
}

function getSections() {
  const ids = new Set();
  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  }
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
  return response.json();
}

function publishCartUpdate(cart, sections, action = 'update') {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: cart.items.map((item) => ({ id: cartLineIdentifier(item), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections,
      items: cart.items,
      itemCount: cart.item_count,
      source: '2thrv-gift-sync',
      didError: false,
    },
  });
}

async function addGift(variantId, propertyName, sectionIds) {
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  body.append(`properties[${propertyName}]`, 'true');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));
  body.append('sections_url', window.location.pathname);

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
  });
  const result = await response.json();
  if (!response.ok || result.errors || result.status) {
    throw new Error(result.description || result.message || 'Unable to add gift');
  }
  return result.sections || {};
}

async function updateGiftLines(updates, sectionIds) {
  let result = null;

  for (const [id, quantity] of Object.entries(updates)) {
    const payload = { id, quantity: Number(quantity) };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    result = await response.json();
    if (!response.ok || result.errors || result.status) {
      throw new Error(result.description || result.errors || 'Unable to update gift');
    }
  }

  return result || fetchCart();
}

async function normalizeGiftLines(cart, lines, qualifies, sectionIds) {
  const updates = {};
  if (!qualifies) {
    for (const line of lines) updates[cartLineIdentifier(line)] = 0;
  } else if (lines.length > 0) {
    const [keptGift, ...duplicates] = lines;
    if (keptGift.quantity !== 1) updates[cartLineIdentifier(keptGift)] = 1;
    for (const duplicate of duplicates) updates[cartLineIdentifier(duplicate)] = 0;
  }

  if (!Object.keys(updates).length) return cart;
  const updatedCart = await updateGiftLines(updates, sectionIds);
  publishCartUpdate(updatedCart, updatedCart.sections || {});
  return updatedCart;
}

async function synchronizeSubscriptionGift(cartItems = null) {
  const sectionIds = getSections();
  let cart = Array.isArray(cartItems) ? { items: cartItems } : await fetchCart();
  const qualifies = hasQualifyingSubscription(cart);
  const giftLines = cart.items.filter(isSubscriptionGiftLine);

  if (qualifies && giftLines.length === 0) {
    const sections = await addGift(BLACK_BOTTLE_VARIANT_ID, SUBSCRIPTION_GIFT_PROPERTY, sectionIds);
    cart = await fetchCart();
    publishCartUpdate(cart, sections);
    return cart;
  }

  return normalizeGiftLines(cart, giftLines, qualifies, sectionIds);
}

async function synchronizeCreatorGift() {
  let cart = await fetchCart();
  const unauthorizedLines = cart.items.filter(
    (item) => Number(item.variant_id) === CREATOR_GIFT_VARIANT_ID && !isAuthorizedCreatorVariantLine(item)
  );
  if (unauthorizedLines.length) {
    const updates = Object.fromEntries(unauthorizedLines.map((line) => [cartLineIdentifier(line), 0]));
    cart = await updateGiftLines(updates, getSections());
    publishCartUpdate(cart, cart.sections || {});
  }
  const giftLines = cart.items.filter(isCreatorGiftLine);
  const qualifies = hasQualifyingCreatorPurchase(cart);
  const hasValidGift = giftLines.some(creatorGiftIsFree);
  return normalizeGiftLines(cart, giftLines, qualifies && (creatorGiftValidationPending || hasValidGift), getSections());
}

async function prepareCreatorGift() {
  creatorGiftValidationPending = true;
  const sectionIds = getSections();
  let cart = await fetchCart();
  if (!hasQualifyingCreatorPurchase(cart)) {
    creatorGiftValidationPending = false;
    return { prepared: false };
  }

  let giftLines = cart.items.filter(isCreatorGiftLine);
  if (giftLines.length === 0) {
    const sections = await addGift(CREATOR_GIFT_VARIANT_ID, CREATOR_GIFT_PROPERTY, sectionIds);
    cart = await fetchCart();
    giftLines = cart.items.filter(isCreatorGiftLine);
    publishCartUpdate(cart, sections);
  }

  await normalizeGiftLines(cart, giftLines, true, sectionIds);
  return { prepared: true };
}

async function finalizeCreatorGift() {
  creatorGiftValidationPending = false;
  const cart = await fetchCart();
  const creatorGiftLines = cart.items.filter(isCreatorGiftLine);
  const validGiftLines = creatorGiftLines.filter(creatorGiftIsFree);
  const updates = {};

  if (!hasQualifyingCreatorPurchase(cart) || validGiftLines.length === 0) {
    for (const line of creatorGiftLines) updates[cartLineIdentifier(line)] = 0;
  } else {
    const [keptGift] = validGiftLines;
    for (const line of creatorGiftLines) {
      const lineId = cartLineIdentifier(line);
      const keptId = cartLineIdentifier(keptGift);
      if (lineId !== keptId || line.quantity !== 1) updates[lineId] = lineId === keptId ? 1 : 0;
    }
  }

  if (Object.keys(updates).length) {
    const updatedCart = await updateGiftLines(updates, getSections());
    publishCartUpdate(updatedCart, updatedCart.sections || {});
    return { valid: false };
  }

  sessionStorage.removeItem(CREATOR_REFERRAL_STORAGE_KEY);
  return { valid: validGiftLines.length > 0 };
}

async function getCreatorGiftRemovalUpdates() {
  const cart = await fetchCart();
  return Object.fromEntries(cart.items.filter(isCreatorGiftLine).map((line) => [cartLineIdentifier(line), 0]));
}

async function synchronizeGifts(cartItems = null) {
  if (activeSync) return activeSync;
  activeSync = (async () => {
    let cart = await synchronizeSubscriptionGift(cartItems);
    cart = await synchronizeCreatorGift();
    return cart;
  })()
    .catch((error) => console.error('[2THRV gift] Synchronization failed', error))
    .finally(() => {
      activeSync = null;
    });
  return activeSync;
}

function queueGiftSync(cartItems = null) {
  if (Array.isArray(cartItems)) queuedCartItems = cartItems;
  if (syncQueued) return;
  syncQueued = true;
  queueMicrotask(() => {
    syncQueued = false;
    const items = queuedCartItems;
    queuedCartItems = null;
    synchronizeGifts(items);
  });
}

async function prepareReferralGiftIfNeeded() {
  if (sessionStorage.getItem(CREATOR_REFERRAL_STORAGE_KEY) !== 'true') return;
  const result = await prepareCreatorGift();
  if (!result.prepared) return;
  window.setTimeout(() => finalizeCreatorGift().catch((error) => console.error('[2THRV gift] Validation failed', error)), 1500);
}

window.THRVCreatorGift = {
  prepareForDiscount: prepareCreatorGift,
  finalizeAfterDiscount: finalizeCreatorGift,
  getRemovalUpdates: getCreatorGiftRemovalUpdates,
};
document.documentElement.dataset.thrvCreatorGiftEngine = 'ready';

let giftSyncLoadingDepth = 0;

function setGiftSyncLoading(active) {
  giftSyncLoadingDepth = active ? giftSyncLoadingDepth + 1 : Math.max(0, giftSyncLoadingDepth - 1);
  document.documentElement.classList.toggle('thrv-gift-syncing', giftSyncLoadingDepth > 0);
}

async function waitForCartUiItemCount(expectedCount, timeout = 5000) {
  const expected = String(Number(expectedCount));
  const startedAt = performance.now();

  while (performance.now() - startedAt < timeout) {
    const countPills = document.querySelectorAll('.cart-drawer__count-pill, [ref="cartItemCount"]');
    if (Array.from(countPills).some((pill) => pill.textContent?.trim() === expected)) return;
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.detail?.source === '2thrv-gift-sync') return;

  setGiftSyncLoading(true);
  const syncPromise = event.promise
    ? event.promise.then(async ({ detail }) => {
        await synchronizeGifts(detail?.items);
        await prepareReferralGiftIfNeeded();
        const finalCart = await fetchCart();
        await waitForCartUiItemCount(finalCart.item_count);
      })
    : synchronizeGifts().then(() => prepareReferralGiftIfNeeded());

  syncPromise
    .catch(() => {})
    .finally(() => setGiftSyncLoading(false));
});

rememberReferralFromUrl();
if (window.location.pathname === '/products/2thrv-whole-food-fuel-gel-3-pack') {
  window.location.replace('/products/whole-food-fuel-gel');
}
window.addEventListener('pageshow', () => {
  queueGiftSync();
  prepareReferralGiftIfNeeded();
});
window.addEventListener(
  'load',
  () => {
    queueGiftSync();
    prepareReferralGiftIfNeeded();
  },
  { once: true }
);

export {
  BLACK_BOTTLE_VARIANT_ID,
  CREATOR_GIFT_PROPERTY,
  CREATOR_GIFT_VARIANT_ID,
  SUBSCRIPTION_GIFT_PROPERTY,
  WHITE_BOTTLE_VARIANT_ID,
};
